// src/pages/Inscricoes/Inscricoes.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import { Search, Download, Plus, SlidersHorizontal, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import styles from "../Clientes/Clientes.module.css"; // reutiliza o CSS
import "./SubscriptionsList.css"; // mantive caso tenha regras extras próprias
import SubscriptionModal from "./SubscriptionsModal";
import CreateSubscriptionModal from "./CreateSubscriptionModal";
import Pagination from "@/components/Pagination/Pagination";

// Sort icon component
function SortIcon({ field, currentSort, currentOrder }: { field: string; currentSort: string; currentOrder: 'asc' | 'desc' }) {
  if (currentSort !== field) {
    return <ChevronsUpDown size={14} className={styles.sortIconInactive} />;
  }
  return currentOrder === 'asc' 
    ? <ChevronUp size={14} className={styles.sortIconActive} />
    : <ChevronDown size={14} className={styles.sortIconActive} />;
}

type Product = { id: string; name: string; [k: string]: any };
type Customer = { id: string; name?: string; email?: string; legalRepresentativeName?: string; [k: string]: any };
type SubscriptionItem = {
  id: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  coworkingId?: string;
  product?: Product | null;
  customer?: Customer | null;
  [k: string]: any;
};
type PaginatedResult<T> = { page: number; pageSize: number; total: number; items: T[]; };


function downloadCsvFromObjects(filename: string, rows: Record<string, any>[]) {
  if (!rows || rows.length === 0) {
    const blob = new Blob([""], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  const columns = Array.from(rows.reduce((acc: Set<string>, r) => { Object.keys(r).forEach(k => acc.add(k)); return acc; }, new Set<string>()));
  const header = columns.map(c => `"${c.replace(/"/g, '""')}"`).join(",");
  const lines = rows.map(r => columns.map(c => {
    const v = r[c] ?? "";
    const val = (v instanceof Date) ? v.toISOString() : String(v);
    return `"${val.replace(/"/g, '""')}"`;
  }).join(","));
  const csv = [header, ...lines].join("\r\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: "Ativo",
  CANCELLED: "Cancelado",
  EXPIRED: "Expirado",
  PENDING: "Pendente",
  SUSPENDED: "Suspenso",
};

function getStatusLabel(status?: string): string {
  if (!status) return "—";
  return STATUS_LABELS[status.toUpperCase()] ?? status;
}

function useDebounce<T>(value: T, delay = 300) {
  const [deb, setDeb] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDeb(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return deb;
}

export default function Inscricoes() {
  const userContext = useUser();
  const token = userContext?.userData?.auth;

  const [q, setQ] = useState("");
  const debQ = useDebounce(q, 300);
  const [statusFilter, setStatusFilter] = useState<string>("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  
  // New filter states
  const [productFilter, setProductFilter] = useState<string>("");
  const [startDateFrom, setStartDateFrom] = useState("");
  const [startDateTo, setStartDateTo] = useState("");
  const [endDateFrom, setEndDateFrom] = useState("");
  const [endDateTo, setEndDateTo] = useState("");
  const [sortBy, setSortBy] = useState<string>("startDate");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [showFilters, setShowFilters] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selected, setSelected] = useState<SubscriptionItem | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Fetch products for filter dropdown
  const { data: productsData } = useQuery({
    queryKey: ['products-for-filter'],
    queryFn: async () => {
      const resp: any = await api.get('/product', {
        params: { page: 1, pageSize: 100 },
        headers: { Authorization: `Bearer ${token}` },
      });
      const raw = (resp && (resp as any).data) ? (resp as any).data : resp;
      return raw.items ?? [];
    },
    enabled: !!token,
  });

  const products = productsData ?? [];

  // Handle column sort
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setQ('');
    setStatusFilter('');
    setProductFilter('');
    setStartDateFrom('');
    setStartDateTo('');
    setEndDateFrom('');
    setEndDateTo('');
    setSortBy('startDate');
    setSortOrder('desc');
  };

  const hasActiveFilters = q || statusFilter || productFilter || startDateFrom || startDateTo || endDateFrom || endDateTo;

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debQ, statusFilter, productFilter, startDateFrom, startDateTo, endDateFrom, endDateTo, sortBy, sortOrder]);

  // React Query for paginated subscriptions
  const {
    data,
    isLoading: loadingInitial,
    refetch,
  } = useQuery({
    queryKey: ['subscriptions', currentPage, pageSize, debQ, statusFilter, productFilter, startDateFrom, startDateTo, endDateFrom, endDateTo, sortBy, sortOrder],
    queryFn: async () => {
      const baseParams: any = {};
      if (statusFilter) baseParams.status = statusFilter;
      if (productFilter) baseParams.productId = productFilter;
      if (startDateFrom) baseParams.startDateFrom = startDateFrom;
      if (startDateTo) baseParams.startDateTo = startDateTo;
      if (endDateFrom) baseParams.endDateFrom = endDateFrom;
      if (endDateTo) baseParams.endDateTo = endDateTo;
      if (sortBy) baseParams.sortBy = sortBy;
      if (sortOrder) baseParams.sortOrder = sortOrder;

      const normalizedQuery = (debQ ?? "").trim().toLowerCase();

      // Quando há termo de busca, carregamos mais itens e filtramos localmente
      // para incluir Empresa, Cliente e Representante Legal de forma consistente.
      if (normalizedQuery.length > 0) {
        const accumulated: SubscriptionItem[] = [];
        let pageNumber = 1;
        const pageSizeServer = 200;
        const MAX_ITEMS = 2000;
        let totalServer = 0;

        while (true) {
          const resp: any = await api.get(`/subscriptions/serialized/coworking`, {
            params: { ...baseParams, page: pageNumber, limit: pageSizeServer },
            headers: { Authorization: `Bearer ${token}` },
          });
          const rawData: PaginatedResult<SubscriptionItem> = (resp && (resp as any).data) ? (resp as any).data : resp;
          const batch = rawData.items ?? [];
          totalServer = rawData.total ?? totalServer;
          accumulated.push(...batch);

          if (accumulated.length >= totalServer) break;
          if (accumulated.length >= MAX_ITEMS) break;
          if (batch.length < pageSizeServer) break;
          pageNumber++;
        }

        const filtered = accumulated.filter((it) => {
          const fields = [
            it.customer?.name ?? "",
            it.customer?.legalRepresentativeName ?? "",
            it.customer?.email ?? "",
            it.product?.name ?? "",
          ];
          return fields.some((f) => String(f).toLowerCase().includes(normalizedQuery));
        });

        const start = (currentPage - 1) * pageSize;
        const end = start + pageSize;
        return { items: filtered.slice(start, end), total: filtered.length };
      }

      const resp: any = await api.get(`/subscriptions/serialized/coworking`, {
        params: { ...baseParams, page: currentPage, limit: pageSize },
        headers: { Authorization: `Bearer ${token}` },
      });
      const rawData: PaginatedResult<SubscriptionItem> = (resp && (resp as any).data) ? (resp as any).data : resp;
      return { items: rawData.items ?? [], total: rawData.total ?? 0 };
    },
    enabled: !!token,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  function handleUpdatedSubscription(updated: SubscriptionItem) {
    setSelected(updated);
    // React Query will automatically show updated data after refetch
  }

  const counts = useMemo(() => {
    const totalLocal = items.length;
    const active = items.filter(i => (i.status ?? "").toUpperCase() === "ACTIVE").length;
    const inactive = totalLocal - active;
    return { total: totalLocal, active, inactive };
  }, [items]);

  function openModalFor(item: SubscriptionItem) {
    setSelected(item);
    setModalOpen(true);
  }

  function exportFilteredToCsv() {
    const rows = items.map(it => ({
      id: it.id,
      empresa: it.customer?.name ?? "",
      cliente: it.customer?.legalRepresentativeName ?? "",
      customerEmail: it.customer?.email ?? "",
      product: it.product?.name ?? "",
      startDate: it.startDate ?? "",
      endDate: it.endDate ?? "",
      status: getStatusLabel(it.status),
    }));
    downloadCsvFromObjects(`inscricoes_${new Date().toISOString().slice(0, 10)}.csv`, rows);
  }

  return (
    <div style={{ width: "100%" }}>
      <div className={styles.clientesHeader}>
        <div className={styles.headerTextGroup}>
          <h1 className={styles.gradientHeading}>Inscrições</h1>
          <p className={`${styles.pageSubtitle} dashboard-page-subtitle`}>
            Acompanhe assinaturas, status e ciclo de renovação dos clientes.
          </p>
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className={styles.addButton} onClick={exportFilteredToCsv} title="Exportar CSV">
            <Download size={14} />&nbsp;Exportar
          </button>

          <button className={styles.addButton} onClick={() => setCreateModalOpen(true)} title="Nova Inscrição">
            <Plus size={14} />&nbsp;Nova Inscrição
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className={styles.filtersSection}>
        <div className={styles.searchContainer}>
          <div className={styles.searchInputContainer} style={{ minWidth: 280 }}>
            <Search className={styles.searchIcon} />
            <input
              className={styles.searchInput}
              placeholder="Buscar por empresa, cliente, representante legal ou produto"
              value={q}
              onChange={e => setQ(e.target.value)}
            />
          </div>

          <select 
            value={statusFilter} 
            onChange={e => setStatusFilter(e.target.value)} 
            className={styles.filterSelect}
          >
            <option value="">Todos os status</option>
            <option value="ACTIVE">Ativo</option>
            <option value="CANCELLED">Cancelado</option>
            <option value="EXPIRED">Expirado</option>
            <option value="PENDING">Pendente</option>
            <option value="SUSPENDED">Suspenso</option>
          </select>

          <button 
            className={`${styles.filterToggle} ${showFilters ? styles.filterToggleActive : ''}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal size={16} />
            Mais filtros {hasActiveFilters && <span className={styles.filterBadge}>●</span>}
          </button>

          {hasActiveFilters && (
            <button className={styles.clearFilters} onClick={clearFilters}>
              <X size={14} /> Limpar
            </button>
          )}
        </div>

        {showFilters && (
          <div className={styles.filtersRow}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Produto</label>
              <select 
                className={styles.filterSelect}
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
              >
                <option value="">Todos os produtos</option>
                {products.map((p: any) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Início (de)</label>
              <input
                type="date"
                className={styles.filterInput}
                value={startDateFrom}
                onChange={(e) => setStartDateFrom(e.target.value)}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Início (até)</label>
              <input
                type="date"
                className={styles.filterInput}
                value={startDateTo}
                onChange={(e) => setStartDateTo(e.target.value)}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Fim (de)</label>
              <input
                type="date"
                className={styles.filterInput}
                value={endDateFrom}
                onChange={(e) => setEndDateFrom(e.target.value)}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Fim (até)</label>
              <input
                type="date"
                className={styles.filterInput}
                value={endDateTo}
                onChange={(e) => setEndDateTo(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      <div className={styles.clientesTableCard}>
        <div className={styles.clientesTableContainer}>
          {loadingInitial ? (
            <div style={{ padding: 16 }}>Carregando...</div>
          ) : (
            <table className={styles.clientesTable}>
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Cliente</th>
                  <th>Produto</th>
                  <th 
                    className={styles.sortableHeader} 
                    onClick={() => handleSort('startDate')}
                  >
                    <span className={styles.thContent}>
                      Início
                      <SortIcon field="startDate" currentSort={sortBy} currentOrder={sortOrder} />
                    </span>
                  </th>
                  <th 
                    className={styles.sortableHeader} 
                    onClick={() => handleSort('endDate')}
                  >
                    <span className={styles.thContent}>
                      Fim
                      <SortIcon field="endDate" currentSort={sortBy} currentOrder={sortOrder} />
                    </span>
                  </th>
                  <th 
                    className={styles.sortableHeader} 
                    onClick={() => handleSort('status')}
                  >
                    <span className={styles.thContent}>
                      Status
                      <SortIcon field="status" currentSort={sortBy} currentOrder={sortOrder} />
                    </span>
                  </th>
                  <th>Preço</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.id}>
                    <td data-label="Empresa">{it.customer?.name ?? "—"}</td>
                    <td data-label="Cliente">{it.customer?.legalRepresentativeName ?? it.customer?.email ?? "—"}</td>
                    <td data-label="Produto">{it.product?.name ?? "—"}</td>
                    <td data-label="Início">{it.startDate ? it.startDate.slice(0, 10) : "—"}</td>
                    <td data-label="Fim">{it.endDate ? it.endDate.slice(0, 10) : "—"}</td>
                    <td data-label="Status">{getStatusLabel(it.status)}</td>
                    <td data-label="Preço">{it.product?.price ? `R$ ${parseFloat(it.product.price).toFixed(2).replace('.', ',')}` : "—"}</td>
                    <td data-label="Ações" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <button className={styles.editButton} onClick={() => openModalFor(it)}>+Detalhes</button>
                      {copiedId === it.id && <span style={{ marginLeft: 6, color: "#16a34a", fontSize: 12 }}>Copiado</span>}
                    </td>
                  </tr>
                ))}

                {items.length === 0 && !loadingInitial && (
                  <tr><td colSpan={8} style={{ padding: 16, textAlign: "center" }}>Nenhuma inscrição encontrada.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        totalItems={total}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
      />

      {modalOpen && selected && (
        <SubscriptionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          subscription={selected}
          onSaved={handleUpdatedSubscription}
        />
      )}

      {createModalOpen && (
        <CreateSubscriptionModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={() => {
            setCreateModalOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
